import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  Renderer2,
  TemplateRef,
} from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { pairwise } from 'rxjs/operators';
import { GasInputBoolean } from '@cikrf/gas-utils/decorators';

import { GasForm } from '../../forms/gas-form.namespace';
import { GasFormKeyDirective } from '../../utils/gas-form-key/gas-form-key.directive';
import { GasTable } from '../gas-table/gas-table.namespace';
import { GasObjectHelper } from '@cikrf/gas-utils/helpers';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import FilterButtonCustomFunctions = GasTable.FilterButtonCustomFunctions;

/** @see https://www.figma.com/file/yqBeq1MiGfpdN62SvmA8tg/RTLABS_GAS_DS_1?node-id=3659%3A37072 */

/** @description Компонент - обёртка отображающий процесс фильтрации */
/** @example
 * <gas-filters [displayed]="displayed"
 *              (filterChanged)="updateFilter($event)">
 *    <gas-input label="Фамилия"
 *               gasFormKey="lastName"
 *               value="Иванов">
 *    </gas-input>
 * </gas-filters>
 */
@UntilDestroy()
@Component({
  selector: 'gas-filters',
  templateUrl: './gas-filters.component.html',
  styleUrls: ['./gas-filters.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GasFiltersComponent {

  @GasInputBoolean()
  @Input()
  public displayed: string | boolean = false;

  @GasInputBoolean()
  @Input()
  public verticalView: string | boolean = false;

  @Input()
  public customFilterButtonsTemplate: TemplateRef<HTMLElement>;

  //TODO упростить конструкцию
  @Input()
  public filterButtonCustomFunctions: FilterButtonCustomFunctions;

  @Input()
  public set textPayload(texts: GasTable.Texts) {
    this.texts = GasObjectHelper.mergeDeep(GasTable.DEFAULT_TEXTS, texts);
  }

  public get textPayload(): GasTable.Texts {
    return this.texts;
  }

  public set state(state: GasForm.InputState) {
    this.innerStateSubject$.next(state);
  }

  public get state(): GasForm.InputState {
    return this.innerStateSubject$.getValue();
  }

  public applied$ = new EventEmitter();

  public undoCurrentChanged$ = new EventEmitter();

  public clearedAllFilters$ = new EventEmitter();

  public clearCurrentFilters$ = new EventEmitter<GasFormKeyDirective>();

  public texts = GasTable.DEFAULT_TEXTS;

  public disabledApplyButtonSubject$ = new BehaviorSubject<boolean>(false);

  public get isApplyBtnDisabled(): boolean {
    return this.isDefault || this.isSuccess;
  }

  private innerStateSubject$ = new BehaviorSubject<GasForm.InputState>(GasForm.InputState.Default);

  public get isDefault(): boolean {
    return this.state === GasForm.InputState.Default;
  }

  public get isPrimary(): boolean {
    return this.state === GasForm.InputState.Primary;
  }

  public get isSuccess(): boolean {
    return this.state === GasForm.InputState.Success;
  }

  @HostBinding('class._hidden')
  public get isHidden(): boolean {
    return !this.displayed;
  }

  @HostBinding('class._vertical-view')
  public get isVerticalView(): boolean {
    return this.verticalView as boolean;
  }

  constructor(
    public changeDetectorRef: ChangeDetectorRef,
    private renderer: Renderer2,
    private elementRef: ElementRef,
  ) {
    this.innerStateSubject$.asObservable()
      .pipe(
        pairwise(),
        untilDestroyed(this),
      )
      .subscribe(([oldState, newState]: [GasForm.InputState, GasForm.InputState]) => {
        const element: HTMLElement = this.elementRef.nativeElement;

        if (oldState) {
          this.renderer.removeClass(element, `_${oldState}`);
        }

        const stateClass = `_${newState}`;

        this.renderer.addClass(element, stateClass);

        this.changeDetectorRef.detectChanges();
      });
  }

  public onApplyBtnClick(): void {
    if (this.isApplyBtnDisabled) {
      return;
    }

    this.applied$.emit();

    if (this.filterButtonCustomFunctions?.applyCallback) {
      this.filterButtonCustomFunctions.applyCallback();
    }
  }

  public onUndoBtnClick(): void {
    this.undoCurrentChanged$.emit();

    if (this.filterButtonCustomFunctions?.cancelCallback) {
      this.filterButtonCustomFunctions.cancelCallback();
    }
  }

  public onClearBtnClick(): void {
    this.clearedAllFilters$.emit();

    if (this.filterButtonCustomFunctions?.resetCallback) {
      this.filterButtonCustomFunctions.resetCallback();
    }
  }
}
